"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOktonAuth = exports.useOptionalOktonAuth = exports.OktonAuthProvider = void 0;
const react_1 = __importStar(require("react"));
const AuthContext = (0, react_1.createContext)(undefined);
function randomString(size = 48) {
    const bytes = new Uint8Array(size);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes).map(value => value.toString(16).padStart(2, '0')).join('');
}
function base64Url(bytes) {
    let binary = '';
    bytes.forEach(byte => { binary += String.fromCharCode(byte); });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
async function challengeFor(verifier) {
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64Url(new Uint8Array(digest));
}
function decodeJwt(token) {
    if (!token)
        return undefined;
    try {
        const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload.padEnd(Math.ceil(payload.length / 4) * 4, '=');
        return JSON.parse(decodeURIComponent(Array.prototype.map.call(window.atob(padded), (c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join('')));
    }
    catch (_a) {
        return undefined;
    }
}
function userFromToken(token) {
    const payload = decodeJwt(token);
    if (!payload || !payload.sub)
        return undefined;
    if (payload.exp && Number(payload.exp) * 1000 <= Date.now())
        return undefined;
    return {
        id: String(payload.sub),
        name: String(payload.name || payload.preferred_username || payload.email || 'Usuário'),
        email: payload.email ? String(payload.email) : undefined,
    };
}
function tokenRemainingMs(token) {
    const payload = decodeJwt(token);
    if (!(payload === null || payload === void 0 ? void 0 : payload.exp))
        return 0;
    return Number(payload.exp) * 1000 - Date.now();
}
const OktonAuthProvider = ({ systemId, platformBaseUrl, children }) => {
    const prefix = `@okton:auth:${systemId}`;
    const [bootstrap, setBootstrap] = (0, react_1.useState)();
    const [accessToken, setAccessToken] = (0, react_1.useState)(() => sessionStorage.getItem(`${prefix}:access_token`) || undefined);
    const [user, setUser] = (0, react_1.useState)(() => {
        const idToken = sessionStorage.getItem(`${prefix}:id_token`) || undefined;
        const storedAccessToken = sessionStorage.getItem(`${prefix}:access_token`) || undefined;
        return userFromToken(idToken || storedAccessToken);
    });
    const [isLoading, setIsLoading] = (0, react_1.useState)(true);
    const [error, setError] = (0, react_1.useState)();
    const refreshInFlight = (0, react_1.useRef)(null);
    function applyTokens(tokens) {
        sessionStorage.setItem(`${prefix}:access_token`, tokens.access_token);
        sessionStorage.setItem(`${prefix}:id_token`, tokens.id_token || '');
        if (tokens.refresh_token) {
            sessionStorage.setItem(`${prefix}:refresh_token`, tokens.refresh_token);
        }
        setAccessToken(tokens.access_token);
        setUser(userFromToken(tokens.id_token || tokens.access_token));
        setError(undefined);
    }
    async function refreshBffSession() {
        if (refreshInFlight.current) {
            return refreshInFlight.current;
        }
        const refreshToken = sessionStorage.getItem(`${prefix}:refresh_token`);
        if (!refreshToken) {
            return false;
        }
        const promise = (async () => {
            var _a;
            try {
                const response = await fetch(`${platformBaseUrl.replace(/\/$/, '')}/v1/auth/${encodeURIComponent(systemId)}/refresh`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refresh_token: refreshToken }),
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok || !((_a = payload.data) === null || _a === void 0 ? void 0 : _a.access_token)) {
                    if (response.status === 401 || response.status === 403) {
                        sessionStorage.removeItem(`${prefix}:access_token`);
                        sessionStorage.removeItem(`${prefix}:id_token`);
                        sessionStorage.removeItem(`${prefix}:refresh_token`);
                        setAccessToken(undefined);
                        setUser(undefined);
                    }
                    return false;
                }
                applyTokens(payload.data);
                return true;
            }
            catch (_b) {
                return false;
            }
            finally {
                refreshInFlight.current = null;
            }
        })();
        refreshInFlight.current = promise;
        return promise;
    }
    (0, react_1.useEffect)(() => {
        let active = true;
        async function load() {
            try {
                const response = await fetch(`${platformBaseUrl.replace(/\/$/, '')}/v1/bootstrap/${encodeURIComponent(systemId)}`, { credentials: 'include' });
                if (!response.ok) {
                    throw new Error('Não foi possível carregar a configuração de autenticação.');
                }
                const payload = await response.json();
                const nextBootstrap = payload.data;
                if (active) {
                    setBootstrap(nextBootstrap);
                }
                if (nextBootstrap.auth.mode === 'keycloak-bff') {
                    const storedAccessToken = sessionStorage.getItem(`${prefix}:access_token`) || undefined;
                    const refreshToken = sessionStorage.getItem(`${prefix}:refresh_token`) || undefined;
                    if (refreshToken &&
                        (!storedAccessToken || tokenRemainingMs(storedAccessToken) <= 60000)) {
                        await refreshBffSession();
                    }
                    else if (storedAccessToken && active) {
                        setAccessToken(storedAccessToken);
                        const idToken = sessionStorage.getItem(`${prefix}:id_token`) || undefined;
                        setUser(userFromToken(idToken || storedAccessToken));
                    }
                }
            }
            catch (err) {
                if (active) {
                    setError(err instanceof Error
                        ? err.message
                        : 'Falha ao carregar autenticação.');
                }
            }
            finally {
                if (active) {
                    setIsLoading(false);
                }
            }
        }
        load();
        return () => {
            active = false;
        };
    }, [platformBaseUrl, systemId]);
    (0, react_1.useEffect)(() => {
        if ((bootstrap === null || bootstrap === void 0 ? void 0 : bootstrap.auth.mode) !== 'keycloak-bff' || !accessToken) {
            return;
        }
        let cancelled = false;
        let refreshTimer;
        const refreshIfNeeded = async (thresholdMs) => {
            const currentToken = sessionStorage.getItem(`${prefix}:access_token`) || undefined;
            if (!currentToken ||
                tokenRemainingMs(currentToken) <= thresholdMs) {
                await refreshBffSession();
            }
        };
        const remaining = tokenRemainingMs(accessToken);
        const delay = Math.max(1000, remaining - 60000);
        refreshTimer = window.setTimeout(async () => {
            if (!cancelled) {
                await refreshBffSession();
            }
        }, delay);
        const handleResume = () => {
            if (!cancelled &&
                document.visibilityState === 'visible') {
                void refreshIfNeeded(120000);
            }
        };
        window.addEventListener('focus', handleResume);
        document.addEventListener('visibilitychange', handleResume);
        return () => {
            cancelled = true;
            if (refreshTimer !== undefined) {
                window.clearTimeout(refreshTimer);
            }
            window.removeEventListener('focus', handleResume);
            document.removeEventListener('visibilitychange', handleResume);
        };
    }, [bootstrap === null || bootstrap === void 0 ? void 0 : bootstrap.auth.mode, accessToken, platformBaseUrl, systemId]);
    async function login(username, password) {
        var _a;
        if (!bootstrap)
            throw new Error('Configuração de autenticação indisponível.');
        if (bootstrap.auth.mode === 'keycloak-bff') {
            if (!username || !password)
                throw new Error('Informe e-mail e senha.');
            setError(undefined);
            const response = await fetch(`${platformBaseUrl.replace(/\/$/, '')}/v1/auth/${encodeURIComponent(systemId)}/login`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !((_a = payload.data) === null || _a === void 0 ? void 0 : _a.access_token)) {
                const message = payload.message || 'Não foi possível entrar.';
                setError(message);
                throw new Error(message);
            }
            applyTokens(payload.data);
            return;
        }
        if (bootstrap.auth.mode !== 'keycloak' || !bootstrap.auth.realm) {
            throw new Error(`Modo de autenticação ${bootstrap.auth.mode} ainda não possui fluxo central habilitado.`);
        }
        const state = randomString(24);
        const verifier = randomString(48);
        const challenge = await challengeFor(verifier);
        sessionStorage.setItem(`${prefix}:state`, state);
        sessionStorage.setItem(`${prefix}:verifier`, verifier);
        const params = new URLSearchParams({
            client_id: bootstrap.auth.clientId,
            redirect_uri: bootstrap.auth.redirectUri,
            response_type: 'code',
            scope: bootstrap.auth.scopes.join(' '),
            state,
            code_challenge: challenge,
            code_challenge_method: 'S256',
        });
        window.location.assign(`${bootstrap.auth.serverUrl}/realms/${encodeURIComponent(bootstrap.auth.realm)}/protocol/openid-connect/auth?${params.toString()}`);
    }
    async function completeLogin() {
        if (!bootstrap || !bootstrap.auth.realm)
            throw new Error('Configuração de autenticação indisponível.');
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const expectedState = sessionStorage.getItem(`${prefix}:state`);
        const verifier = sessionStorage.getItem(`${prefix}:verifier`);
        if (!code || !state || !expectedState || state !== expectedState || !verifier) {
            throw new Error('Retorno de autenticação inválido ou expirado.');
        }
        const body = new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: bootstrap.auth.clientId,
            code,
            redirect_uri: bootstrap.auth.redirectUri,
            code_verifier: verifier,
        });
        const response = await fetch(`${bootstrap.auth.serverUrl}/realms/${encodeURIComponent(bootstrap.auth.realm)}/protocol/openid-connect/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
        });
        if (!response.ok)
            throw new Error('Não foi possível concluir o login.');
        const tokens = await response.json();
        applyTokens(tokens);
        sessionStorage.removeItem(`${prefix}:state`);
        sessionStorage.removeItem(`${prefix}:verifier`);
    }
    async function requestPasswordReset(email) {
        const response = await fetch(`${platformBaseUrl.replace(/\/$/, '')}/v1/auth/${encodeURIComponent(systemId)}/forgot-password`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload.message || 'Não foi possível solicitar a recuperação de senha.');
        }
        return payload.message || 'Se o e-mail estiver cadastrado, enviaremos um link para redefinir sua senha.';
    }
    async function logout() {
        const idToken = sessionStorage.getItem(`${prefix}:id_token`);
        const refreshToken = sessionStorage.getItem(`${prefix}:refresh_token`);
        if ((bootstrap === null || bootstrap === void 0 ? void 0 : bootstrap.auth.mode) === 'keycloak-bff' && refreshToken) {
            const response = await fetch(`${platformBaseUrl.replace(/\/$/, '')}/v1/auth/${encodeURIComponent(systemId)}/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });
            if (!response.ok) {
                throw new Error('Não foi possível encerrar a sessão no SSO.');
            }
        }
        sessionStorage.removeItem(`${prefix}:access_token`);
        sessionStorage.removeItem(`${prefix}:id_token`);
        sessionStorage.removeItem(`${prefix}:refresh_token`);
        setAccessToken(undefined);
        setUser(undefined);
        if ((bootstrap === null || bootstrap === void 0 ? void 0 : bootstrap.auth.mode) === 'keycloak' && bootstrap.auth.realm) {
            const postLogout = new URL(bootstrap.auth.postLogoutUrl || '/login', window.location.origin).toString();
            const params = new URLSearchParams({ post_logout_redirect_uri: postLogout, client_id: bootstrap.auth.clientId });
            if (idToken)
                params.set('id_token_hint', idToken);
            window.location.assign(`${bootstrap.auth.serverUrl}/realms/${encodeURIComponent(bootstrap.auth.realm)}/protocol/openid-connect/logout?${params.toString()}`);
        }
    }
    const value = (0, react_1.useMemo)(() => ({
        bootstrap,
        user,
        accessToken,
        isLoading,
        isAuthenticated: Boolean(user && accessToken),
        error,
        login,
        completeLogin,
        logout,
        requestPasswordReset,
    }), [bootstrap, user, accessToken, isLoading, error]);
    return react_1.default.createElement(AuthContext.Provider, { value: value }, children);
};
exports.OktonAuthProvider = OktonAuthProvider;
function useOptionalOktonAuth() {
    return (0, react_1.useContext)(AuthContext);
}
exports.useOptionalOktonAuth = useOptionalOktonAuth;
function useOktonAuth() {
    const context = useOptionalOktonAuth();
    if (!context)
        throw new Error('useOktonAuth deve ser usado dentro de OktonAuthProvider.');
    return context;
}
exports.useOktonAuth = useOktonAuth;
//# sourceMappingURL=OktonAuthProvider.js.map